import { useState, useEffect } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────
export const N8N_BASE = "https://n8n.lacopohomelab.com/webhook";

export const FF =
  "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', 'Helvetica Neue', sans-serif";

// ─── Frosted glass palette ────────────────────────────────────────────────────
export const glass = {
  background: "rgba(255,255,255,0.60)",
  backdropFilter: "blur(60px) saturate(180%) brightness(1.02)",
  WebkitBackdropFilter: "blur(60px) saturate(180%) brightness(1.02)",
};

// Input / select — glass with dark text
const BASE_INPUT = {
  padding: "10px 13px",
  background: "rgba(255,255,255,0.52)",
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  border: "0.5px solid rgba(255,255,255,0.82)",
  borderRadius: 11,
  color: "#1c1c1e",
  fontSize: 14,
  width: "100%",
  transition: "border-color 0.2s",
  fontFamily: FF,
};
export const INP = BASE_INPUT;
export const SEL = {
  padding: "10px 13px",
  paddingRight: 32,
  background: "rgba(255,255,255,0.52)",
  border: "0.5px solid rgba(255,255,255,0.82)",
  borderRadius: 11,
  color: "#1c1c1e",
  fontSize: 14,
  width: "100%",
  transition: "border-color 0.2s",
  fontFamily: FF,
  cursor: "pointer",
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='rgba(60%2C60%2C67%2C0.4)' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center",
  backgroundSize: "10px",
};

export const fmt = (n) =>
  Number(n).toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ─── Responsive hook ──────────────────────────────────────────────────────────
export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [breakpoint]);
  return isMobile;
}

// ─── Global CSS ───────────────────────────────────────────────────────────────
export const GLASS_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
  * { box-sizing: border-box; }
  body { margin: 0; background: linear-gradient(160deg, #ececf0 0%, #e8e8ed 45%, #ebebf0 100%); min-height: 100vh; }

  /* Input focus */
  .g-input:focus {
    outline: none;
    border-color: rgba(255,255,255,0.95) !important;
    box-shadow: none !important;
  }
  .g-input::placeholder { color: rgba(60,60,67,0.28); }
  .g-select option { background: #ebebef; color: #1c1c1e; }

  /* Hover states */
  .mode-btn:hover { background: rgba(255,255,255,0.72) !important; border-color: rgba(255,255,255,0.9) !important; }
  .submit-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  .submit-btn:active:not(:disabled) { transform: translateY(0); }
  .drop-item:hover { background: rgba(0,0,0,0.04) !important; }
  .back-btn:hover { color: rgba(60,60,67,0.7) !important; }
  .nav-item:hover { background: rgba(255,255,255,0.55) !important; }
  .assign-btn:hover { background: rgba(255,255,255,0.72) !important; border-color: rgba(255,255,255,0.95) !important; }

  /* Skeleton animation */
  @keyframes skpulse { 0%,100% { opacity: 1 } 50% { opacity: 0.4 } }

  /* Scrollbar */
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 4px; }

  /* Responsive grids */
  .form-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 14px;
  }
  .form-grid-3 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    margin-bottom: 12px;
  }
  .page-pad {
    min-height: 100vh;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 40px 24px 60px;
  }
  .glass-card-outer {
    border-radius: 20px;
  }

  /* Mobile */
  @media (max-width: 768px) {
    .sidebar-desktop { display: none !important; }
    .mobile-nav { display: flex !important; }
    .page-pad { padding: 20px 14px 96px; }
    .form-grid-2 { grid-template-columns: 1fr !important; }
    .form-grid-3 { grid-template-columns: 1fr 1fr !important; }
    .glass-card-outer { border-radius: 16px !important; }
    .inv-amount-flex { flex-wrap: wrap; }
    .inv-amount-flex > div { flex: 1 1 calc(33% - 6px); min-width: 80px; }
  }
  @media (min-width: 769px) {
    .mobile-nav { display: none !important; }
  }
`;

// ─── Canonical stat card — use this on every page, never define a local one ──
export function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{
      flex: 1, minWidth: 0,
      padding: "14px 16px",
      background: accent || "rgba(255,255,255,0.42)",
      backdropFilter: "blur(20px) saturate(180%)",
      WebkitBackdropFilter: "blur(20px) saturate(180%)",
      border: "0.5px solid rgba(255,255,255,0.78)",
      borderRadius: 14,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 600,
        color: "rgba(60,60,67,0.42)",
        letterSpacing: "0.7px", textTransform: "uppercase",
        fontFamily: FF, marginBottom: 7,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 22, fontWeight: 700,
        color: "#1c1c1e", letterSpacing: "-0.5px", fontFamily: FF,
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: "rgba(60,60,67,0.42)", fontFamily: FF, marginTop: 3 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// ─── Skeleton loader — use everywhere instead of "Loading…" text ─────────────
export function SkeletonRows({ count = 5, height = 56, gap = 8, radius = 12 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap }}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            height,
            borderRadius: radius,
            background: "rgba(255,255,255,0.45)",
            border: "0.5px solid rgba(255,255,255,0.78)",
            animation: "skpulse 1.4s ease-in-out infinite",
            animationDelay: `${i * 0.07}s`,
          }}
        />
      ))}
    </div>
  );
}

// ─── Shared tab bar (use this everywhere — matches Campaigns/Invoices style) ──
export function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{
      display: "flex", gap: 3, padding: 4,
      background: "rgba(0,0,0,0.05)",
      borderRadius: 13,
    }}>
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            flex: 1, padding: "8px 16px",
            borderRadius: 10,
            border: active === t.id
              ? "0.5px solid rgba(255,255,255,0.85)"
              : "0.5px solid transparent",
            background: active === t.id ? "rgba(255,255,255,0.65)" : "transparent",
            color: active === t.id ? "#1c1c1e" : "rgba(60,60,67,0.5)",
            fontSize: 13, fontWeight: active === t.id ? 600 : 400,
            cursor: "pointer", fontFamily: FF,
            transition: "background 0.15s, color 0.15s, border-color 0.15s",
            whiteSpace: "nowrap",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}
        >
          {t.label}
          {t.badge > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 20,
              background: "rgba(255,149,0,0.13)", color: "rgba(150,80,0,0.9)",
              border: "0.5px solid rgba(255,149,0,0.22)",
            }}>
              {t.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ─── Helper components ────────────────────────────────────────────────────────

export function GF({ label, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <Lbl text={label} required={required} />
      {children}
    </div>
  );
}

export function Lbl({ text, required }) {
  return (
    <label
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "rgba(60,60,67,0.48)",
        letterSpacing: "0.6px",
        textTransform: "uppercase",
        fontFamily: FF,
      }}
    >
      {text}
      {required && <span style={{ color: "rgba(180,50,50,0.7)", marginLeft: 2 }}>*</span>}
    </label>
  );
}

export function SPill({ status }) {
  const m = {
    active:           { bg: "rgba(52,199,89,0.12)",   c: "#1e7a3c",              border: "rgba(52,199,89,0.2)"   },
    awaiting_payment: { bg: "rgba(255,149,0,0.11)",   c: "#8c5000",              border: "rgba(255,149,0,0.2)"   },
    completed:        { bg: "rgba(80,80,220,0.09)",   c: "#3a3aaa",              border: "rgba(80,80,220,0.15)"  },
    draft:            { bg: "rgba(100,105,130,0.08)", c: "rgba(60,60,67,0.55)",  border: "rgba(100,105,130,0.12)"},
    assigned:         { bg: "rgba(60,110,220,0.09)",  c: "#2250a0",              border: "rgba(60,110,220,0.15)" },
    sent:             { bg: "rgba(255,149,0,0.11)",   c: "#8c5000",              border: "rgba(255,149,0,0.2)"   },
    paid:             { bg: "rgba(52,199,89,0.12)",   c: "#1e7a3c",              border: "rgba(52,199,89,0.2)"   },
  };
  const s = m[status] || { bg: "rgba(0,0,0,0.05)", c: "rgba(60,60,67,0.55)", border: "rgba(0,0,0,0.08)" };
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 20,
        background: s.bg,
        color: s.c,
        border: `1px solid ${s.border}`,
        fontFamily: FF,
        textTransform: "capitalize",
        letterSpacing: "0.2px",
      }}
    >
      {status?.replace(/_/g, " ")}
    </span>
  );
}

export function PR({ label, value, vc }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: 12, color: "rgba(60,60,67,0.45)", fontFamily: FF }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: vc || "#1c1c1e", fontFamily: FF }}>{value}</span>
    </div>
  );
}

// Frosted glass card — liquid glass on grey background
export function GlassCard({ children, style = {}, maxWidth = 680 }) {
  return (
    <div
      className="glass-card-outer"
      style={{
        width: "100%",
        maxWidth,
        background: "rgba(255,255,255,0.52)",
        backdropFilter: "blur(40px) saturate(180%)",
        WebkitBackdropFilter: "blur(40px) saturate(180%)",
        border: "0.5px solid rgba(255,255,255,0.85)",
        borderRadius: 20,
        position: "relative",
        zIndex: 1,
        overflow: "visible",
        ...style,
      }}
    >
      {children}
    </div>
  );
}
